import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { OpaqueWorkspaceString } from './types';
import { OPAQUE_PREFIX } from './types';
import { toSnapshot } from './snapshot';

/** Base64 encode that handles Unicode (works in both browser and Node.js). */
function toBase64(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/** Base64 decode that handles Unicode (works in both browser and Node.js). */
function fromBase64(b64: string): string {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/**
 * Serialize a MolecularWorkspace to an opaque versioned string.
 *
 * Format: `alchemie:ws:v1:` + base64(JSON.stringify(snapshot))
 *
 * This is the recommended format for external transport (Slack, Jira, clipboard,
 * image metadata). The string is opaque — do NOT compare strings for equality.
 * Use graph-based comparison from workspace-compare instead.
 */
export function toOpaqueString(workspace: MolecularWorkspace): OpaqueWorkspaceString {
  const snapshot = toSnapshot(workspace);
  const json = JSON.stringify(snapshot);
  return (OPAQUE_PREFIX + toBase64(json)) as OpaqueWorkspaceString;
}

/**
 * Deserialize an opaque workspace string back to a snapshot JSON string.
 * Returns undefined if the string doesn't have the expected prefix.
 */
export function decodeOpaquePayload(input: string): string | undefined {
  if (!input.startsWith(OPAQUE_PREFIX)) return undefined;
  const payload = input.slice(OPAQUE_PREFIX.length);
  return fromBase64(payload);
}
